import {
  Box,
  Button,
  Flex,
  HStack,
  Spinner,
  Text,
  VStack,
  useToast
} from "@chakra-ui/react"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import OptionsMenu, {
  OptionsMenuItemProps
} from "@pathwright/ux/src/components/menu/OptionsMenu"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import moment from "moment"
import React from "react"
import {
  InvitationFragment,
  useDeleteInvitationMutation,
  useResendInvitationMutation
} from "../api/generated"
import { useCopyLink } from "../share/link/CopyLink"
import { getInvitationRoleConfig } from "./utils"

const tPrefix = "share_card.track_tab"

const renderErrorToast = ({
  message,
  onClose
}: {
  message: string
  onClose: () => void
}) => (
  <Box bg="TERTIARY_WHITE_BACKGROUND" borderRadius="8px" p={5}>
    <Flex alignItems="center" justifyContent="space-between">
      <Flex alignItems="center">
        <Pathicon icon="caution-circle" />
        <Text size="xxs" ml={2}>
          {message}
        </Text>
      </Flex>
      <Button
        onClick={onClose}
        type="button"
        variant="ghost"
        p={0}
        minWidth="auto"
      >
        <Pathicon icon="x" />
      </Button>
    </Flex>
  </Box>
)

const Invitation = ({
  invitation,
  onRevoke,
  onResend
}: {
  // NOTE: the status field is added locally in Invitations.tsx
  invitation: InvitationFragment & { status: string }
  onRevoke?: () => void
  onResend?: () => void
}) => {
  const handleCopy = useCopyLink(invitation.invitation_url)

  const { t, tca } = useTranslate()

  const toast = useToast()

  const [revokeInvitation, { loading: revokeLoading, error: revokeError }] =
    useDeleteInvitationMutation()
  const [resendInvitation, { loading: resendLoading, error: resendError }] =
    useResendInvitationMutation()

  const handleRevoke = async (invitation: InvitationFragment) => {
    await revokeInvitation({
      variables: {
        deleteInvitationId: invitation.id
      }
    })

    if (onRevoke) onRevoke()
  }

  const handleResend = async (invitation: InvitationFragment) => {
    await resendInvitation({
      variables: {
        resendInvitationId: invitation.id
      }
    })

    if (onResend) onResend()
  }

  const sentTimeDate =
    invitation.invitation_email?.created_dtime || invitation.created_dtime

  const roleKey = getInvitationRoleConfig(invitation).type
  const role = tca(`role_labels.verb.${roleKey}`)
  const sentTime = moment(sentTimeDate).fromNow()

  const sent = t(`${tPrefix}.{{role}} invite sent {{sentTime}}`, {
    role,
    sentTime
  })

  const inviteOptions: OptionsMenuItemProps[] = [
    {
      label: "Revoke",
      icon: "recur",
      onClick: () => handleRevoke(invitation)
    },
    {
      label: "Resend",
      icon: "minus-circle",
      onClick: () => handleResend(invitation)
    },
    {
      label: "Copy Link",
      icon: "copy",
      onClick: handleCopy
    }
  ]

  React.useEffect(() => {
    const error = revokeError || resendError
    if (error) {
      toast({
        position: "bottom",
        duration: 2000,
        isClosable: true,
        render: () =>
          renderErrorToast({ message: error.message, onClose: toast.closeAll })
      })
    }
  }, [revokeError, resendError])

  return (
    <HStack alignItems="center" my={3}>
      <Flex
        bgColor="gray.50"
        color="gray.900"
        borderRadius="50%"
        w="40px"
        h="40px"
        alignItems="center"
        justifyContent="center"
        mr={2}
      >
        <Pathicon icon="envelope" />
      </Flex>
      <VStack alignItems="flex-start" spacing={0} noOfLines={1} flex={1}>
        <Text
          size="xs"
          fontWeight="bold"
          lineHeight="24px"
          color="gray.900"
          m={0}
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {invitation.to_email}
        </Text>
        <Text size="xxs" m={0} color="gray.400" noOfLines={1}>
          {sent}
        </Text>
      </VStack>
      <HStack>
        <Text size="xs" fontWeight={600} color="gray.900" m={0}>
          {invitation.status}
        </Text>
        {revokeLoading || resendLoading ? (
          <Spinner size="sm" />
        ) : (
          <OptionsMenu
            options={inviteOptions}
            menuProps={{
              placement: "bottom-end"
            }}
          />
        )}
      </HStack>
    </HStack>
  )
}

export default Invitation
